import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  TextField,
  Alert,
  Chip,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { doc, updateDoc, getDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';

interface AdminNote {
  note: string;
  timestamp: any; // Firestore Timestamp
  addedBy: string;
}

interface SessionData {
  id: string;
  sessionID: string;
  userEmail: string;
  lotName: string;
  lotID?: string;
  spaceNumber?: string;
  startDate: string;
  startTime: string;
  prepaidHours?: number;
  duration?: number; // minutes
  currency?: string;
  rate?: number;
  adminNotes?: AdminNote[];
  vehicle?: {
    licensePlate: string;
    make: string;
    color: string;
    nickname?: string;
  };
}

interface ManageSessionDialogProps {
  open: boolean;
  onClose: () => void;
  session: SessionData | null;
  onSuccess: () => void;
}

export type { SessionData, AdminNote };

export default function ManageSessionDialog({ open, onClose, session, onSuccess }: ManageSessionDialogProps) {
  // Who is actually doing this. Every write used to be stamped with a
  // hardcoded actor string, so a note left by a partner was
  // indistinguishable from one left by ARCS staff.
  const actor = () => auth.currentUser?.email || 'unknown';

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [view, setView] = useState<'menu' | 'addTime' | 'endSession' | 'addNote'>('menu');
  const [hoursToAdd, setHoursToAdd] = useState('1');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const resetState = () => {
    setView('menu');
    setHoursToAdd('1');
    setNote('');
    setLoading(false);
    setError('');
    setSuccess('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleEndSession = async () => {
    if (!session) return;
    setLoading(true);
    setError('');
    try {
      const now = new Date();
      await updateDoc(doc(db, 'parkingsessions', session.id), {
        sessionActive: false,
        endDate: now.toISOString().split('T')[0],
        endTime: now.toTimeString().split(' ')[0],
        adminEndedBy: actor(),
        adminEndedAt: Timestamp.now(),
      });
      setSuccess('Session ended successfully.');
      setTimeout(() => {
        handleClose();
        onSuccess();
      }, 1000);
    } catch (err: any) {
      setError('Failed to end session: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTime = async () => {
    if (!session) return;
    const hours = parseFloat(hoursToAdd);
    if (isNaN(hours) || hours <= 0) {
      setError('Please enter a valid number of hours.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const sessionDoc = await getDoc(doc(db, 'parkingsessions', session.id));
      if (!sessionDoc.exists()) throw new Error('Session not found');

      const data = sessionDoc.data();
      const currentDuration = data.duration || 0; // in minutes
      const newDuration = currentDuration + (hours * 60);

      await updateDoc(doc(db, 'parkingsessions', session.id), {
        duration: newDuration,
        adminExtendedBy: actor(),
        adminExtendedAt: Timestamp.now(),
        adminExtendedHours: hours,
      });
      setSuccess(`Added ${hours} hour(s) to session. New total: ${Math.ceil(newDuration / 60)}h.`);
      setTimeout(() => {
        handleClose();
        onSuccess();
      }, 1500);
    } catch (err: any) {
      setError('Failed to add time: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!session || !note.trim()) return;
    setLoading(true);
    setError('');
    try {
      const sessionDoc = await getDoc(doc(db, 'parkingsessions', session.id));
      if (!sessionDoc.exists()) throw new Error('Session not found');

      const data = sessionDoc.data();
      const existingNotes = data.adminNotes || [];

      await updateDoc(doc(db, 'parkingsessions', session.id), {
        adminNotes: [...existingNotes, {
          note: note.trim(),
          timestamp: Timestamp.now(),
          addedBy: actor(),
        }],
      });
      setSuccess('Note added.');
      setNote('');
      setTimeout(() => {
        setSuccess('');
        setView('menu');
      }, 1000);
    } catch (err: any) {
      setError('Failed to add note: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate time remaining / overdue
  const getTimeStatus = () => {
    if (!session?.startDate || !session?.startTime || !session?.prepaidHours) return null;
    try {
      const startDateTime = new Date(`${session.startDate} ${session.startTime}`);
      const endDateTime = new Date(startDateTime.getTime() + (session.prepaidHours * 60 * 60 * 1000));
      const now = new Date();
      const diffMs = endDateTime.getTime() - now.getTime();
      const diffMinutes = Math.round(diffMs / (1000 * 60));

      if (diffMinutes > 0) {
        const h = Math.floor(diffMinutes / 60);
        const m = diffMinutes % 60;
        return { label: h > 0 ? `${h}h ${m}m remaining` : `${m}m remaining`, color: 'success' as const };
      } else {
        const overMinutes = Math.abs(diffMinutes);
        const h = Math.floor(overMinutes / 60);
        const m = overMinutes % 60;
        return { label: h > 0 ? `${h}h ${m}m overdue` : `${m}m overdue`, color: 'error' as const };
      }
    } catch {
      return null;
    }
  };

  if (!session) return null;

  const timeStatus = getTimeStatus();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <span>Manage Session</span>
        {timeStatus && (
          <Chip label={timeStatus.label} color={timeStatus.color} size="small" />
        )}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Session Details (always visible) */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Session Details</Typography>
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Typography variant="body2"><strong>Lot:</strong> {session.lotName}</Typography>
            <Typography variant="body2"><strong>Space:</strong> {session.spaceNumber || 'N/A'}</Typography>
            <Typography variant="body2"><strong>Started:</strong> {session.startDate} {session.startTime}</Typography>
            <Typography variant="body2"><strong>Prepaid:</strong> {session.prepaidHours ? `${session.prepaidHours}h` : 'N/A'}</Typography>
            <Typography variant="body2"><strong>Email:</strong> {session.userEmail}</Typography>
            <Typography variant="body2"><strong>Currency:</strong> {session.currency || 'N/A'}</Typography>
          </Box>
          {session.vehicle && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Vehicle:</strong> {session.vehicle.make} — {session.vehicle.color} — {session.vehicle.licensePlate}
                {session.vehicle.nickname ? ` (${session.vehicle.nickname})` : ''}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Action Views */}
        {view === 'menu' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={() => setView('addTime')}
              sx={{ justifyContent: 'flex-start', py: 1.5, textTransform: 'none' }}
            >
              <Box>
                <Typography variant="body1" fontWeight={600}>Add Time</Typography>
                <Typography variant="caption" color="text.secondary">Extend the session duration</Typography>
              </Box>
            </Button>

            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={() => setView('endSession')}
              sx={{ justifyContent: 'flex-start', py: 1.5, textTransform: 'none' }}
            >
              <Box>
                <Typography variant="body1" fontWeight={600}>End Session</Typography>
                <Typography variant="caption" color="text.secondary">Force end this parking session</Typography>
              </Box>
            </Button>

            <Button
              variant="outlined"
              color="info"
              fullWidth
              onClick={() => setView('addNote')}
              sx={{ justifyContent: 'flex-start', py: 1.5, textTransform: 'none' }}
            >
              <Box>
                <Typography variant="body1" fontWeight={600}>Add Note</Typography>
                <Typography variant="caption" color="text.secondary">Attach an admin note to this session</Typography>
              </Box>
            </Button>
          </Box>
        )}

        {view === 'addTime' && (
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Add Time to Session</Typography>
            <TextField
              fullWidth
              label="Hours to Add"
              type="number"
              value={hoursToAdd}
              onChange={(e) => setHoursToAdd(e.target.value)}
              inputProps={{ step: "0.5", min: "0.5" }}
              helperText={`Current prepaid: ${session.prepaidHours || 0}h → New total: ${(session.prepaidHours || 0) + (parseFloat(hoursToAdd) || 0)}h`}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button variant="outlined" onClick={() => setView('menu')} disabled={loading}>Back</Button>
              <Button variant="contained" onClick={handleAddTime} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : 'Confirm'}
              </Button>
            </Box>
          </Box>
        )}

        {view === 'endSession' && (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This will immediately end the session. The user will be notified that their session has been terminated.
            </Alert>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Are you sure you want to end <strong>{session.vehicle?.licensePlate || session.userEmail}</strong>'s session at <strong>{session.lotName}</strong>?
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" onClick={() => setView('menu')} disabled={loading}>Back</Button>
              <Button variant="contained" color="error" onClick={handleEndSession} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : 'End Session'}
              </Button>
            </Box>
          </Box>
        )}

        {view === 'addNote' && (
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Add Admin Note</Typography>
            <TextField
              fullWidth
              label="Note"
              multiline
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., Customer called about extension, vehicle blocking exit..."
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button variant="outlined" onClick={() => setView('menu')} disabled={loading}>Back</Button>
              <Button variant="contained" onClick={handleAddNote} disabled={loading || !note.trim()}>
                {loading ? <CircularProgress size={20} /> : 'Save Note'}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
